'use client';

import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import OTPInput from 'react-otp-input';
import LoginOptions from 'src/components/app/auth/components/@shared/login-options';
import Props from 'src/components/app/auth/components/otp/type';

import Container from '@/components/@base/container';
import { Input } from '@/components/@base/input';
import AuthForm from '@/components/app/auth/components/@shared/auth-form';
import { LOGIN_BY } from '@/components/app/auth/components/@shared/login-options/type';
import ResendOtp from '@/components/app/auth/components/@shared/resend-otp';

import { STEP, useAuthStepStore } from '@/store/auth.store';

import { pageLevelLocalization } from '@/constant/localization';

const { login: loginLocalization } = pageLevelLocalization;

const convertToEnglishNumbers = (str: string): string => {
  const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return str
    .split('')
    .map((char) => {
      const persianIndex = persianNumbers.indexOf(char);
      const arabicIndex = arabicNumbers.indexOf(char);

      if (persianIndex !== -1) return englishNumbers[persianIndex];
      if (arabicIndex !== -1) return englishNumbers[arabicIndex];
      return char;
    })
    .join('');
};

export default function OtpForm({
  onSubmit,
  handleSendCode,
  isLoading,
  sentToBale = false,
}: Props) {
  const [otp, setOtp] = useState('');
  const { phoneNumber, setStep } = useAuthStepStore();

  const [isWebOtpSupported, setIsWebSupported] = useState(false);
  const webOtpAbortRef = useRef<AbortController | null>(null);
  const isListeningRef = useRef(false);

  const backHandler = () => {
    setStep(STEP.AUTH_STEP_START);
  };

  const requestWebOtp = () => {
    if (!isWebOtpSupported || isListeningRef.current) return;

    if (webOtpAbortRef.current) {
      webOtpAbortRef.current.abort();
    }

    const ac = new AbortController();
    webOtpAbortRef.current = ac;
    isListeningRef.current = true;

    navigator.credentials
      .get({
        otp: { transport: ['sms'] },
        signal: ac.signal,
      } as any)
      .then((res: any) => {
        if (res?.code) {
          const rawCode = String(res.code).trim();
          const normalized = convertToEnglishNumbers(rawCode).replace(/\D/g, '').slice(0, 5);
          if (normalized && normalized.length === 5) {
            setOtp(normalized);
          }
        }
        isListeningRef.current = false;
      })
      .catch((err) => {
        isListeningRef.current = false;
        if (err.name !== 'AbortError') {
          console.log('WebOTP error:', err);
        }
      });
  };

  useEffect(() => {
    const isSupported = typeof window !== 'undefined' && 'OTPCredential' in window;
    setIsWebSupported(isSupported);

    if (isSupported) {
      let metaTag = document.querySelector('meta[name="otp-credential"]');
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', 'otp-credential');
        metaTag.setAttribute('content', 'required');
        document.head.appendChild(metaTag);
      }

      const timer = setTimeout(() => {
        requestWebOtp();
      }, 400);

      return () => {
        clearTimeout(timer);
        if (webOtpAbortRef.current) {
          webOtpAbortRef.current.abort();
        }
      };
    }
  }, [isWebOtpSupported]);

  const handleOtpChange = (value: string) => {
    const englishValue = convertToEnglishNumbers(value);
    const numericValue = englishValue.replace(/\D/g, '');
    if (numericValue.length <= 5) {
      setOtp(numericValue);
    }
  };

  const onSubmitHandler = (currentOtp: string) => {
    onSubmit(currentOtp, () => setOtp(''));
  };

  useEffect(() => {
    if (otp.length === 5) {
      onSubmitHandler(otp);
    }
  }, [otp]);

  return (
    <Formik
      validateOnChange={false}
      validateOnBlur={false}
      initialValues={{
        otp: '',
      }}
      onSubmit={() => onSubmitHandler(otp)}
    >
      {() => {
        return (
          <Form>
            <AuthForm
              title={loginLocalization.enterOtp}
              submitText={loginLocalization.otpVerification}
              phoneNumber={phoneNumber}
              isLoading={isLoading}
              sentToBale={sentToBale}
              backHandler={backHandler}
              actions={
                <Container center className='flex-wrap items-end justify-between gap-y-2 pt-5'>
                  <div className='flex flex-col gap-2'>
                    <ResendOtp
                      handleSendCode={() => {
                        handleSendCode();
                        setOtp('');
                        if (webOtpAbortRef.current) {
                          webOtpAbortRef.current.abort();
                        }
                        isListeningRef.current = false;
                        setTimeout(() => {
                          requestWebOtp();
                        }, 150);
                      }}
                    />
                  </div>
                  <LoginOptions loginBy={LOGIN_BY.PASSWORD} />
                </Container>
              }
            >
              <Container dir='ltr' className='w-full'>
                <OTPInput
                  value={otp}
                  onChange={handleOtpChange}
                  numInputs={5}
                  inputType='text'
                  shouldAutoFocus={true}
                  containerStyle='flex justify-center gap-[4px] sm:gap-3'
                  renderInput={(props, index) => (
                    <Input
                      {...props}
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      inputMode='numeric'
                      containerClassName='max-w-[60px] min-w-[40px] flex-1'
                      className='aspect-square h-auto min-h-0 w-full! px-0 text-center text-xl focus:border-[#FFC87B] focus:shadow-[0px_2px_6px_0px_rgba(255,150,6,0.19)] sm:text-2xl'
                    />
                  )}
                />
              </Container>
            </AuthForm>
          </Form>
        );
      }}
    </Formik>
  );
}
